<template>
  <div>
    <div class="pg-head" v-if="!embedded">
      <div class="pg-ico"><span class="mdi mdi-calendar-remove"></span></div>
      <div>
        <div class="id-h1">{{ $t('holidays.title') }}</div>
        <div class="pg-sub">{{ $t('holidays.subtitle') }}</div>
      </div>
    </div>

    <id-data-grid
      :columns="headers"
      :rows="holidayList"
      :search-keys="['description']"
      :search-placeholder="$t('btn.search')"
      :add-label="$t('holidays.newHoliday')"
      :loading="loadingHolidays"
      @add="showAddNewHoliday">
      <template #cell-holidaydate="{ row }">{{ format_date(row.holidaydate) }}</template>
      <template #cell-actions="{ row }">
        <span class="mdi mdi-delete-forever" style="color:var(--c-error);cursor:pointer;font-size:20px;padding:0 5px" @click="deleteItem(row)"></span>
      </template>
    </id-data-grid>

    <!-- add dialog -->
    <id-modal v-model="dialog" size="sm" icon="mdi-calendar-star" :title="formTitle">
      <div class="modal-sec"><span class="mdi mdi-calendar-remove"></span> {{ $t('holidays.holidayInfo') }}</div>
      <div class="form-grid">
        <div class="field full"><label>{{ $t('table.holidayDate') }} <span class="req">*</span></label>
          <!-- Mondays are already auto-closed, so block them here (and keep the legend explaining it) -->
          <id-date v-model="editedItem.holidaydate" :disable-mondays="true" :placeholder="$t('common.selectDate')"></id-date></div>
        <div class="field full"><label>{{ $t('table.description') }} <span class="req">*</span></label>
          <input class="id-input" v-model="editedItem.description"></div>
      </div>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="close">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" :disabled="!editedItem.holidaydate || !editedItem.description" @click="save">
          <span class="mdi mdi-content-save"></span> {{ $t('btn.save') }}</button>
      </template>
    </id-modal>

    <!-- delete confirm dialog -->
    <id-modal v-model="dialogHolidayDelete" size="sm" icon="mdi-delete-alert-outline" :title="$t('common.confirmDeleteTitle')" persistent>
      <p style="margin:0">{{ $t('holidays.confirmDelete', { date: format_date(editedItem.holidaydate) }) }}</p>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="clickCancelDeleteHoliday">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" style="background:var(--c-error)" @click="clickConfirmDeleteHoliday">
          <span class="mdi mdi-delete"></span> {{ $t('btn.ok') }}</button>
      </template>
    </id-modal>

    <!-- booking-impact warning: backend found existing bookings on the chosen date
         (requireConfirm) — show the list, re-submit with confirmed:true on confirm -->
    <id-modal v-model="dialogBookingWarn" size="md" icon="mdi-alert" :title="$t('closures.bookingWarnTitle')" persistent>
      <p style="margin:0 0 10px">{{ $t('closures.bookingWarnLead', { date: format_date(pendingPayload && pendingPayload.holidaydate), n: affected.total }) }}</p>
      <div v-if="affected.group.length" class="modal-sec"><span class="mdi mdi-account-group"></span> {{ $t('closures.bookingWarnGroup', { n: affected.group.length }) }}</div>
      <ul v-if="affected.group.length" class="warn-list">
        <li v-for="(g, i) in affected.group" :key="'g' + i">{{ g.classtime }} {{ g.coursename }} — {{ g.nickname }}</li>
      </ul>
      <div v-if="affected.private.length" class="modal-sec mt"><span class="mdi mdi-account-heart-outline"></span> {{ $t('closures.bookingWarnPrivate', { n: affected.private.length }) }}</div>
      <ul v-if="affected.private.length" class="warn-list">
        <li v-for="(p, i) in affected.private" :key="'p' + i">{{ p.classtime }} — {{ p.coachusername }} — {{ p.students }}</li>
      </ul>
      <p style="margin:10px 0 0;color:var(--c-error)">{{ $t('closures.bookingWarnNote') }}</p>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="closeBookingWarn">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" style="background:var(--c-error)" @click="confirmSaveAnyway">
          <span class="mdi mdi-alert"></span> {{ $t('closures.bookingWarnConfirm') }}</button>
      </template>
    </id-modal>
  </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { mapGetters } from 'vuex';
  import moment from 'moment'

  export default {
    props: {
      // when hosted inside the merged ClosureManagement page, hide this component's own pg-head
      embedded: { type: Boolean, default: false },
    },
    data: () => ({
      dialog: false,
      dialogHolidayDelete: false,
      dialogBookingWarn: false,
      loadingHolidays: false,
      holidayList: [],
      editedIndex: -1,
      // bookings the backend found on the chosen date (requireConfirm flow)
      affected: { group: [], private: [], total: 0 },
      pendingPayload: null,
      editedItem: {
        holidaydate: null,
        description: null,
      },
      defaultItem: {
        holidaydate: null,
        description: null,
      },
    }),
    computed: {
        formTitle() {
          return this.editedIndex === -1 ? this.$t('holidays.newHoliday') : this.$t('holidays.editHoliday')
        },
        headers() {
          return [
            { title: this.$t('table.holidayDate'), key: 'holidaydate', sortable: true },
            { title: this.$t('table.description'), key: 'description', sortable: false, align: 'start' },
            { title: this.$t('table.delete'), key: 'actions', sortable: false, align: 'center' },
          ]
        },
        ...mapGetters({
            token: 'getToken',
        }),
    },
    async created() {
      await this.initialize();
    },
    methods: {
      async initialize() {
        // menu-entry load: skeleton for the real fetch only (no artificial $minLoad)
        this.loadingHolidays = true;
        const token = this.$store.getters.getToken;
        await axios.get(this.baseURL+'/collectHolidays',{
            headers: {
                Authorization: `Bearer ${token}`,
            }
        })
          .then(response => {
            this.holidayList = response.data.data;
          })
          .catch(() => {
            //console.error(error);
          });
        this.loadingHolidays = false;
      },
      async deleteItem(item) {
        this.editedIndex = this.holidayList.indexOf(item);
        this.editedItem = Object.assign({}, item);
        this.dialogHolidayDelete = true;
        
      },
      async clickConfirmDeleteHoliday() {
        const token = this.$store.getters.getToken;
        await axios.delete(this.baseURL+`/holidays/${this.editedItem.id}`,{
            headers: {
                Authorization: `Bearer ${token}`,
            }
        })
          .then(() => {
            this.initialize();
          })
          .catch(() => {
            //console.error(error);
          });
          this.dialogHolidayDelete = false;
      },
      clickCancelDeleteHoliday() {
          this.dialogHolidayDelete = false;
          this.$nextTick(() => {
              this.editedItem = Object.assign({}, this.defaultItem);
              this.editedIndex = -1;
          });
      },
      async save() {
        const token = this.$store.getters.getToken;
        const payload = {
          holidaydate: this.SQLDate(this.editedItem.holidaydate),
          description: this.editedItem.description,
        };
        const cfg = { headers: { Authorization: `Bearer ${token}` } };
        try {
          const res = this.editedItem.id
            ? await axios.put(this.baseURL + `/holidays/${this.editedItem.id}`, payload, cfg)
            : await axios.post(this.baseURL + '/holidays', payload, cfg);
          if (res.data && res.data.success === false) {
            // date already has bookings — show the impact list and ask to confirm
            if (res.data.requireConfirm) {
              this.affected = res.data.affected || { group: [], private: [], total: 0 };
              this.pendingPayload = payload;
              this.dialogBookingWarn = true;
              return;
            }
            // backend rejects duplicate dates with { success:false } — show it, keep the dialog open
            this.$emit('onErrorHandler', res.data.message || this.$t('msg.addFail'));
            return;
          }
          this.close();
          this.initialize();
        } catch (e) {
          this.$emit('onErrorHandler', e.message);
        }
      },
      // re-submit with confirmed:true after the admin acknowledged the impacted bookings
      async confirmSaveAnyway() {
        if (!this.pendingPayload) return;
        const token = this.$store.getters.getToken;
        const cfg = { headers: { Authorization: `Bearer ${token}` } };
        const payload = { ...this.pendingPayload, confirmed: true };
        try {
          const res = this.editedItem.id
            ? await axios.put(this.baseURL + `/holidays/${this.editedItem.id}`, payload, cfg)
            : await axios.post(this.baseURL + '/holidays', payload, cfg);
          if (res.data && res.data.success === false) {
            this.$emit('onErrorHandler', res.data.message || this.$t('msg.addFail'));
            return;
          }
          this.closeBookingWarn();
          this.close();
          this.initialize();
        } catch (e) {
          this.$emit('onErrorHandler', e.message);
        }
      },
      closeBookingWarn() {
        this.dialogBookingWarn = false;
        this.pendingPayload = null;
        this.affected = { group: [], private: [], total: 0 };
      },
      showAddNewHoliday() {
        this.$nextTick(() => {
              this.editedItem = Object.assign({}, this.defaultItem);
              this.editedIndex = -1;
          });
        this.dialog = true;
      },
      close() {
        this.dialog = false;
        this.editedItem = { ...this.defaultItem };
      },
        SQLDate(date) {
            return moment(date).format('YYYY-MM-DD')
        },
        format_date(value) {
            if (value) {
                return moment(value).format('DD/MM/YYYY')
            }
        },
    },
  };
  </script>
  <style scoped>
  .text-center {
    justify-items: center;
  }
  .warn-list {
    margin: 6px 0 0;
    padding-left: 20px;
    max-height: 180px;
    overflow-y: auto;
  }
  </style>